import { useState } from 'react';
import { Outlet, Link, useLocation } from '@tanstack/react-router';
import { LayoutDashboard, FileText, Building2, KeyRound, CalendarRange, CalendarCheck, PiggyBank, Menu, LogOut } from 'lucide-react';
import { Sheet, SheetTrigger, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { GlobalSearch } from '@/components/GlobalSearch';
import { NotificationBell } from '@/components/NotificationBell';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const DRAWER_WIDTH = 260;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: <LayoutDashboard className="h-5 w-5" /> },
  { label: 'Contracts', path: '/contracts', icon: <FileText className="h-5 w-5" /> },
  { label: 'Renewals', path: '/renewals', icon: <CalendarCheck className="h-5 w-5" /> },
  { label: 'Budgets', path: '/budgets', icon: <PiggyBank className="h-5 w-5" /> },
  { label: 'Vendors', path: '/vendors', icon: <Building2 className="h-5 w-5" /> },
  { label: 'Licenses', path: '/licenses', icon: <KeyRound className="h-5 w-5" /> },
  { label: 'Timeline', path: '/timeline', icon: <CalendarRange className="h-5 w-5" /> },
];

function NavContent({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const { user, logout } = useAuth();

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="p-6 pb-4">
        <h1 className="text-lg font-bold text-white tracking-tight">ATA Contract Manager</h1>
        <p className="text-xs text-white/40 mt-1">IT Contract Management</p>
      </div>

      {/* Nav Links */}
      <nav className="flex-1 px-2 space-y-1">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={onNavigate}
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
              isActive(item.path)
                ? 'bg-primary/15 text-primary-light'
                : 'text-white/60 hover:bg-white/8 hover:text-white/90',
            )}
          >
            {item.icon}
            {item.label}
          </Link>
        ))}
      </nav>

      {/* User Info + Logout */}
      <div className="p-4 border-t border-white/8">
        {user ? (
          <>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-primary/20 text-primary-light flex items-center justify-center text-xs font-semibold shrink-0">
                {user.firstName?.[0]}{user.lastName?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white/80 truncate">{user.displayName}</p>
                <p className="text-xs text-white/40 truncate">{user.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="w-full justify-start text-white/50 hover:text-white/80 hover:bg-white/8"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </Button>
          </>
        ) : (
          <p className="text-xs text-white/30 text-center">v1.0.0</p>
        )}
      </div>
    </div>
  );
}

const App: React.FC = () => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const location = useLocation();

  // Don't show sidebar/header on login page
  if (location.pathname === '/login') {
    return <Outlet />;
  }

  return (
    <div className="flex min-h-screen">
      {/* Desktop Sidebar */}
      <aside
        className="hidden md:flex flex-col fixed inset-y-0 left-0 z-30 bg-sidebar-bg"
        style={{ width: DRAWER_WIDTH }}
      >
        <NavContent />
      </aside>

      {/* Mobile Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <div className="md:hidden" />
        <SheetContent side="left">
          <NavContent onNavigate={() => setSheetOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 flex flex-col" style={{ marginLeft: 'var(--sidebar-width, 0px)' }}>
        {/* Top Bar */}
        <header
          className="sticky top-0 z-20 flex items-center h-16 px-4 border-b border-border bg-white/80 backdrop-blur-md"
        >
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden mr-2">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
          </Sheet>

          <h2 className="hidden lg:block text-base font-semibold text-grey-800 flex-1">
            IT Contract Management System
          </h2>

          <GlobalSearch />

          <NotificationBell />
        </header>

        {/* Page Content */}
        <main className="flex-1 bg-background">
          <Outlet />
        </main>
      </div>

      <style>{`
        @media (min-width: 768px) {
          :root { --sidebar-width: ${DRAWER_WIDTH}px; }
        }
      `}</style>
    </div>
  );
};

export default App;
